import React from 'react';
import { FiServer, FiBox, FiLayers, FiDatabase } from 'react-icons/fi';
import { IconType } from 'react-icons';

interface Recommendation {
  service: string;
  current_instance: string;
  recommended_instance: string;
  current_processor?: string;
  processor?: string;
  current_vcpu?: number;
  vcpu?: number;
  current_memory_gb?: number;
  memory_gb?: number;
  count: number;
  current_cost: number;
  new_cost: number;
  savings: number;
  savings_percentage?: number;
}

interface OptimizationRecommendationsTableProps {
  recommendations: Recommendation[];
  loading: boolean;
  selectedService: string | null;
  sortBy: string;
  onServiceFilterChange: (value: string) => void;
  onSortChange: (value: string) => void;
}

const OptimizationRecommendationsTable: React.FC<OptimizationRecommendationsTableProps> = ({
  recommendations,
  loading,
  selectedService,
  sortBy,
  onServiceFilterChange,
  onSortChange,
}) => {
  const formatCurrency = (amount: number | null | undefined) => {
    if (amount == null) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number | null | undefined) => {
    if (num == null) return '0';
    return new Intl.NumberFormat('en-US').format(num);
  };

  const getServiceIcon = (service: string): IconType => {
    const icons: Record<string, IconType> = {
      EC2: FiServer,
      ECS: FiBox,
      EKS: FiLayers,
      RDS: FiDatabase,
      SageMaker: FiServer,
    };
    return icons[service] || FiServer;
  };

  const getServiceIconColor = (_service: string) => {
    return 'text-blue-600 dark:text-blue-400';
  };

  const services = ['All Services', 'EC2', 'ECS', 'EKS', 'RDS', 'SageMaker'];
  const sortOptions = [
    { value: 'highest_savings', label: 'Highest Savings' },
    { value: 'highest_percentage', label: 'Highest Percentage' },
    { value: 'service', label: 'Service' },
  ];

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
      {/* Header with Filters */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <label htmlFor="service" className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Service:
          </label>
          <select id="service"
            value={selectedService || 'all'}
            onChange={(e) => onServiceFilterChange(e.target.value)}
            className="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {services.map((service) => {
              const value = service === 'All Services' ? 'all' : service.toLowerCase();
              return (
                <option key={service} value={value}>
                  {service}
                </option>
              );
            })}
          </select>
        </div>
        <div className="flex items-center gap-4">
          <label htmlFor="sort-by" className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Sort by:
          </label>
          <select id="sort-by"
            value={sortBy}
            onChange={(e) => onSortChange(e.target.value)}
            className="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Title and Count */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          Optimization Recommendations
        </h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          {recommendations.length} recommendation{recommendations.length === 1 ? '' : 's'} found
        </p>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : recommendations.length === 0 ? (
        <div className="px-6 py-12 text-center">
          <p className="text-gray-500 dark:text-gray-400">No recommendations found</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  SERVICE
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  CURRENT INSTANCE
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  RECOMMENDED INSTANCE
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  COUNT
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  CURRENT COST
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  NEW COST
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  SAVINGS
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {recommendations.map((rec) => {
                const ServiceIcon = getServiceIcon(rec.service);
                const iconColor = getServiceIconColor(rec.service);

                return (
                  <tr key={`${rec.service}-${rec.current_instance}`} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <ServiceIcon className={`h-5 w-5 ${iconColor} mr-2`} />
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {rec.service}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {rec.current_instance}
                        </div>
                        <div className="text-gray-500 dark:text-gray-400">
                          {rec.current_processor || rec.processor || 'N/A'}
                        </div>
                        {(rec.current_vcpu || rec.vcpu) && (rec.current_memory_gb || rec.memory_gb) && (
                          <div className="text-gray-500 dark:text-gray-400">
                            {rec.current_vcpu || rec.vcpu} vCPU • {rec.current_memory_gb || rec.memory_gb} GB
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <div className="font-medium text-blue-600 dark:text-blue-400">
                          {rec.recommended_instance}
                        </div>
                        <div className="text-gray-500 dark:text-gray-400">
                          {rec.processor || 'N/A'}
                        </div>
                        {(rec.vcpu && rec.memory_gb) && (
                          <div className="text-gray-500 dark:text-gray-400">
                            {rec.vcpu} vCPU • {rec.memory_gb} GB
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {formatNumber(rec.count)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {formatCurrency(rec.current_cost)}/mo
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {formatCurrency(rec.new_cost)}/mo
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        <div className="font-medium text-green-600 dark:text-green-400">
                          {formatCurrency(rec.savings)}/mo
                        </div>
                        {rec.savings_percentage && (
                          <div className="text-xs text-green-500 dark:text-green-400">
                            {rec.savings_percentage.toFixed(1)}% savings
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default OptimizationRecommendationsTable;
